import {
  createContext, ReactNode, useCallback, useMemo, useState,
} from 'react';
import BlockData from '../models/BlockData';
import api from '../services/api';

export interface BlockDataPerPage {
  id: string,
  params?: BlockData[],
  created_at: string,
  updated_at: string,
  page_setting?: PageSetting
}

interface PageSetting {
  title?:string
  smart_phone_width?: number
  desk_top_width?: number
  header_script?: string
  body_script?: string
}

interface PageContextProps {
  pageIds?: string[]
  pageDatas?: BlockDataPerPage[]
  Create?: (params: string, cb?:(id: string)=>void) => void
  Update?: (id: string, params: string, cb?: () => void) => void
  UpdateSetting?: (id: string, body:PageSetting, cb?: () => void) => void
  List?: () => void
  Get?: (id: string) => void
  Delete?: (id: string) => void
  Copy?: (id: string) => void
  listLoading?: boolean
  pageDataLoading?: boolean
}

export const PagesContext = createContext<PageContextProps>({});
interface PagesContextProviderProps {
  children: ReactNode
}

const PagesContextProvider:React.FC<PagesContextProviderProps> = (props:PagesContextProviderProps) => {
  const { children } = props;
  const [pageDatas, setPageDatas] = useState<BlockDataPerPage[]>([]);
  const [pageIds, setPageIds] = useState<string[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [pageDataLoading, setPageDataLoading] = useState(false);
  const Create = useCallback(async (params: string, cb?:(id:string)=>void) => {
    try {
      const res = await api.post('/v1/pages/create', { params });
      setPageIds((ids) => [...ids.filter((id) => id !== res.data), res.data]);
      if (cb != null) {
        cb(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const Update = useCallback((id: string, params: string, cb?:() => void) => {
    api.patch(`/v1/pages/${id}/update`, { params }).then(() => {
      if (cb != null) {
        cb();
      }
    });
  }, []);

  const UpdateSetting = useCallback((id:string, props: PageSetting, cb?:() => void) => {
    api.patch(`/v1/pages/${id}/setting/update`, props).then(() => {
      if (cb != null) {
        cb();
      }
    });
  }, []);

  const List = useCallback(async () => {
    setListLoading(true);
    const result = await api.get('/v1/pages');
    setListLoading(false);
    const { data } = result;

    if (!Array.isArray(data)) {
      return;
    }

    const allPagesBlockData: BlockDataPerPage[] = [];
    const pageIds: string[] = [];
    for (let i = 0; i < data.length; i++) {
      const obj:BlockDataPerPage = {
        id: data[i].id,
        created_at: data[i].created_at,
        updated_at: data[i].updated_at,
        page_setting: data[i].page_setting,
      };
      if (data[i].params != null && data[i].params.length > 0) {
        try {
          const dataPerPage = JSON.parse(data[i].params) as BlockData[];
          obj.params = dataPerPage;
        } catch (e) {
          console.error('failed at parse params');
        }
      }

      allPagesBlockData.push(obj);
      pageIds.push(data[i].id);
    }

    setPageDatas(allPagesBlockData);
    setPageIds(pageIds);
  }, []);

  const Get = useCallback(async (id: string) => {
    setPageDataLoading(true);
    const result = await api.get(`/v1/pages/${id}`);
    setPageDataLoading(false);
    const { data } = result;

    const blockDataPerPage: BlockDataPerPage = {
      id: data.id,
      created_at: data.created_at,
      updated_at: data.updated_at,
    };

    if (data.params != null && data.params.length > 0) {
      try {
        const params = JSON.parse(data.params) as BlockData[];
        blockDataPerPage.params = params;
      } catch (e) {
        console.error('failed at parse params');
      }
    }

    if (data.page_setting != null) {
      blockDataPerPage.page_setting = {
        title: data.page_setting.title,
        smart_phone_width: data.page_setting.smart_phone_width,
        desk_top_width: data.page_setting.desktop_width,
        header_script: data.page_setting.header_script,
        body_script: data.page_setting.body_script,
      };
    }

    setPageDatas((pageDatas) => {
      if (pageDatas.some((p) => p.id === blockDataPerPage.id)) {
        return pageDatas.map((data) => {
          if (data.id === blockDataPerPage.id) {
            return blockDataPerPage;
          }
          return data;
        });
      }

      return [...pageDatas, blockDataPerPage];
    });
  }, []);

  const Delete = useCallback(async (id: string) => {
    const result = await api.delete(`/v1/pages/${id}/delete`);
    // 削除が失敗になった場合
    if (result.status !== 200) {
      return;
    }
    setPageIds((ids) => ids.filter((_id) => _id !== id));
    setPageDatas((pageDatas) => pageDatas.filter((p) => p.id !== id));
  }, []);

  const Copy = useCallback(async (id: string) => {
    const result = await api.get(`/v1/pages/${id}`);
    const { params } = result.data;
    if (params === null) {
      return;
    }
    Create(params, (id:string) => {
      setPageIds((ids) => [...ids.filter((_id) => _id !== id), id]);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageDatas, Create]);

  const values = useMemo<PageContextProps>(() => ({
    Create,
    List,
    Update,
    UpdateSetting,
    Get,
    Delete,
    pageIds,
    pageDatas,
    Copy,
    listLoading,
    pageDataLoading,
  }), [Create, List, Get, Update, UpdateSetting, pageIds, pageDatas, Delete, Copy, listLoading, pageDataLoading]);

  return (
    <PagesContext.Provider value={values}>
      {children}
    </PagesContext.Provider>
  );
};

export default PagesContextProvider;
